/**
 * Per-integration Field Mapping + Client Preferences dialog.
 *
 * Two tabs:
 *   1. Preferences — sync mode, dedup mode, event & lead-type filters,
 *      attribution prefill.
 *   2. Field Mappings — for every CRM in the integration's `sync_targets`,
 *      admins can map iEvntür lead fields to the CRM's field keys.
 *
 * Consumes:
 *   POST /api/admin/zapier/integrations/{id}/preferences
 *   GET  /api/admin/zapier/integrations/{id}/field-mappings
 *   POST /api/admin/zapier/integrations/{id}/field-mappings
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Settings, Save, Loader2, X, Trash2, Plus,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const SYNC_MODES = [
  { value: 'realtime',  label: 'Real-time',  hint: 'Fan-out fires on every event.' },
  { value: 'scheduled', label: 'Scheduled',  hint: 'Batched — future roadmap. Currently behaves like real-time.' },
  { value: 'paused',    label: 'Paused',     hint: 'No fan-out. Inbound still processed.' },
];

const DEDUP_MODES = [
  { value: 'email',           label: 'Email' },
  { value: 'phone',           label: 'Phone' },
  { value: 'email_or_phone',  label: 'Email or Phone' },
  { value: 'strict',          label: 'Strict (all fields)' },
  { value: 'off',             label: 'Off' },
];

const COMMON_EVENT_FILTERS = [
  'lead.created', 'lead.updated', 'client.signup',
  'talent.signup', 'vendor.signup', 'booking.confirmed',
  'shift.filled', 'timesheet.approved', 'invoice.paid',
];

const LEAD_TYPES = [
  'brand', 'talent', 'agency', 'vendor', 'client', 'partner',
];

// ─────────────────────────────────────────────────────────────
// PREFERENCES TAB
// ─────────────────────────────────────────────────────────────

function PreferencesForm({ integration, onSaved }) {
  const [syncMode, setSyncMode] = useState(integration.sync_mode || 'realtime');
  const [dedupMode, setDedupMode] = useState(integration.dedup_mode || 'email');
  const [eventFilter, setEventFilter] = useState(integration.event_filter || []);
  const [leadTypeFilter, setLeadTypeFilter] = useState(integration.lead_type_filter || []);
  const [attributionJson, setAttributionJson] = useState(
    JSON.stringify(integration.attribution_prefill || {}, null, 2),
  );
  const [busy, setBusy] = useState(false);

  const toggleEvent = (evt) => {
    setEventFilter((p) => (p.includes(evt) ? p.filter((e) => e !== evt) : [...p, evt]));
  };
  const toggleLeadType = (t) => {
    setLeadTypeFilter((p) => (p.includes(t) ? p.filter((e) => e !== t) : [...p, t]));
  };

  const save = async () => {
    let attribution = {};
    try {
      attribution = attributionJson.trim() ? JSON.parse(attributionJson) : {};
    } catch {
      toast.error('Attribution prefill must be valid JSON');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/zapier/integrations/${integration.id}/preferences`,
        {
          sync_mode: syncMode,
          dedup_mode: dedupMode,
          event_filter: eventFilter,
          lead_type_filter: leadTypeFilter,
          attribution_prefill: attribution,
        },
        AUTH(),
      );
      toast.success('Preferences saved');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="preferences-form">
      {/* SYNC MODE */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
          Sync Mode
        </div>
        <div className="flex flex-wrap gap-2">
          {SYNC_MODES.map((m) => (
            <label
              key={m.value}
              className={`flex flex-col gap-0.5 cursor-pointer rounded-lg border px-3 py-2 text-sm ${
                syncMode === m.value
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-slate-200 hover:border-indigo-300'
              }`}
              data-testid={`pref-sync-${m.value}`}
            >
              <div className="flex items-center gap-2 font-medium">
                <input
                  type="radio"
                  checked={syncMode === m.value}
                  onChange={() => setSyncMode(m.value)}
                />
                {m.label}
              </div>
              <span className="text-xs text-slate-500 ml-5">{m.hint}</span>
            </label>
          ))}
        </div>
      </div>

      {/* DEDUP */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
          Dedup Mode
        </div>
        <div className="flex flex-wrap gap-1.5">
          {DEDUP_MODES.map((m) => (
            <button
              key={m.value}
              onClick={() => setDedupMode(m.value)}
              className={`px-2.5 py-1 rounded-full text-xs border transition ${
                dedupMode === m.value
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'
              }`}
              data-testid={`pref-dedup-${m.value}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* EVENT FILTER */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1.5">
          Event Filter <span className="font-normal normal-case text-slate-500">— empty = all events pass</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {COMMON_EVENT_FILTERS.map((evt) => (
            <button
              key={evt}
              onClick={() => toggleEvent(evt)}
              className={`px-2 py-0.5 rounded-full text-xs border transition ${
                eventFilter.includes(evt)
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'
              }`}
              data-testid={`pref-event-chip-${evt}`}
            >
              {evt}
            </button>
          ))}
        </div>
      </div>

      {/* LEAD TYPE FILTER */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1.5">
          Lead-type Filter <span className="font-normal normal-case text-slate-500">— empty = all lead types</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {LEAD_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => toggleLeadType(t)}
              className={`px-2 py-0.5 rounded-full text-xs border capitalize transition ${
                leadTypeFilter.includes(t)
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'
              }`}
              data-testid={`pref-lead-type-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* ATTRIBUTION PREFILL */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1.5">
          Attribution Prefill <span className="font-normal normal-case text-slate-500">— JSON dict merged onto every outbound lead</span>
        </div>
        <Textarea
          data-testid="pref-attribution-json"
          value={attributionJson}
          onChange={(e) => setAttributionJson(e.target.value)}
          className="font-mono text-xs"
          rows={5}
          placeholder='{"campaign":"summer-2026","source_ref":"partner-xyz"}'
        />
      </div>

      <Button onClick={save} disabled={busy} data-testid="pref-save-btn" className="w-full">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Save Preferences
      </Button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// FIELD MAPPINGS TAB
// ─────────────────────────────────────────────────────────────

function CRMMappingEditor({ integrationId, crmKey, mapping, ievnturFields, onSaved }) {
  const [rows, setRows] = useState(
    Object.entries(mapping || {}).map(([src, dst]) => ({ src, dst })),
  );
  const [busy, setBusy] = useState(false);

  const addRow = () => setRows((r) => [...r, { src: '', dst: '' }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const updateRow = (i, key, val) => {
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  };

  const save = async () => {
    const built = {};
    for (const row of rows) {
      if (!row.src) continue;
      built[row.src] = row.dst || '';  // empty dst = explicit drop
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/zapier/integrations/${integrationId}/field-mappings`,
        { crm_key: crmKey, mapping: built },
        AUTH(),
      );
      toast.success(`Mapping saved for ${crmKey}`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid={`mapping-editor-${crmKey}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm flex items-center gap-2">
          {crmKey}
          <Badge variant="outline" className="text-[10px]">{rows.length} mapped</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={addRow}
            data-testid={`mapping-add-row-${crmKey}`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={busy}
            data-testid={`mapping-save-${crmKey}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 text-center py-3">
          No custom mapping — the adapter&apos;s default field names are used.
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-[10px] uppercase tracking-wide text-slate-500 font-semibold px-1">
            <span>iEvntür field</span>
            <span>{crmKey} field</span>
            <span></span>
          </div>
          {rows.map((row, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[1fr_1fr_auto] gap-2"
              data-testid={`mapping-row-${crmKey}-${idx}`}
            >
              <select
                value={row.src}
                onChange={(e) => updateRow(idx, 'src', e.target.value)}
                className="text-xs border rounded-md px-2 py-1"
                data-testid={`mapping-row-src-${crmKey}-${idx}`}
              >
                <option value="">— pick an iEvntür field —</option>
                {(ievnturFields || []).map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label} ({f.key})
                  </option>
                ))}
              </select>
              <Input
                value={row.dst}
                onChange={(e) => updateRow(idx, 'dst', e.target.value)}
                placeholder="e.g. firstName or customFields.event"
                className="text-xs"
                data-testid={`mapping-row-dst-${crmKey}-${idx}`}
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={() => removeRow(idx)}
                data-testid={`mapping-row-remove-${crmKey}-${idx}`}
              >
                <Trash2 className="w-4 h-4 text-rose-600" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="text-[11px] text-slate-500">
        Tip: leave the CRM field blank to <strong>drop</strong> that field from the outbound
        payload. Use dot notation (e.g. <code>customFields.event</code>) to build nested keys.
      </div>
    </div>
  );
}

function FieldMappingsSection({ integration }) {
  const [mappings, setMappings] = useState({});
  const [ievnturFields, setIevnturFields] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/zapier/integrations/${integration.id}/field-mappings`,
        AUTH(),
      );
      setMappings(data.mappings || {});
      setIevnturFields(data.ievntur_fields || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, [integration.id]);

  useEffect(() => { load(); }, [load]);

  const syncTargets = integration.sync_targets || [];

  if (loading) {
    return (
      <div className="text-center py-6 text-slate-500" data-testid="mappings-loading">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading mappings…
      </div>
    );
  }

  if (syncTargets.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-6 text-center border rounded-lg" data-testid="mappings-no-crms">
        This integration has no CRM sync targets configured. Add one from the Integrations tab first.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="field-mappings-section">
      <p className="text-xs text-slate-600">
        Map iEvntür lead fields to each CRM&apos;s field names. Fields not listed here fall through
        to the adapter&apos;s default naming.
      </p>
      {syncTargets.map((crmKey) => (
        <CRMMappingEditor
          key={crmKey}
          integrationId={integration.id}
          crmKey={crmKey}
          mapping={mappings[crmKey] || {}}
          ievnturFields={ievnturFields}
          onSaved={load}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DIALOG (self-contained overlay)
// ─────────────────────────────────────────────────────────────

export default function FieldMappingDialog({ integration, onClose, onSaved }) {
  if (!integration) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="field-mapping-dialog"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-8 max-h-[calc(100vh-4rem)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Settings className="w-5 h-5 text-indigo-600" />
              {integration.name}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Configure sync preferences and field mappings for this integration.
            </p>
          </div>
          <Button
            size="icon"
            variant="ghost"
            onClick={onClose}
            data-testid="field-mapping-dialog-close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          <Tabs defaultValue="preferences">
            <TabsList className="mb-4">
              <TabsTrigger value="preferences" data-testid="field-mapping-tab-preferences">
                Preferences
              </TabsTrigger>
              <TabsTrigger value="mappings" data-testid="field-mapping-tab-mappings">
                Field Mappings
              </TabsTrigger>
            </TabsList>
            <TabsContent value="preferences">
              <PreferencesForm integration={integration} onSaved={onSaved} />
            </TabsContent>
            <TabsContent value="mappings">
              <FieldMappingsSection integration={integration} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
